import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import Home from './pages/Home/Home';
import Services from './pages/Service/Services';
import Mission from './pages/Mission/Mission';
import Contact from './pages/Contact/Contact';
import Credentials from './pages/Credentials/Credentials';
import Management from './pages/Management/Management';
import EPC from './pages/EPC/EPC';
import ErrorBoundary from './components/ErrorBoundary.tsx';
import ScrollToTop from './components/ScrollToTop';
import LoadingBarContainer from './containers/LoadingBarContainer.tsx';
import NavbarContainer from './containers/NavbarContainer.tsx';
import ToastContainer from './containers/ToastContainer.tsx';
import { store } from './store/index.ts';
import { useAppDispatch } from './store/hooks.ts';
import { queryClient } from './lib/queryClient.ts';
import { resetAllState } from './lib/resetAll.ts';
import './App.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

function AppContent() {
  const dispatch = useAppDispatch();
  const appQueryClient = useQueryClient();

  useEffect(() => {
    const handleLogout = () => {
      resetAllState(dispatch, appQueryClient);
    };

    let inactivityTimer: number | undefined;
    const resetInactivityTimer = () => {
      window.clearTimeout(inactivityTimer);
      inactivityTimer = window.setTimeout(() => {
        handleLogout();
      }, SESSION_TIMEOUT_MS);
    };

    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];
    activityEvents.forEach((eventName) => {
      window.addEventListener(eventName, resetInactivityTimer);
    });
    resetInactivityTimer();

    const handleBeforeUnload = () => {
      handleLogout();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.clearTimeout(inactivityTimer);
      activityEvents.forEach((eventName) => {
        window.removeEventListener(eventName, resetInactivityTimer);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [dispatch, appQueryClient]);

  return (
    <Router>
      <ScrollToTop />
      <LoadingBarContainer />
      <div className="App">
        <NavbarContainer />
        <Routes>
          <Route path="/" element={<ErrorBoundary><Home /></ErrorBoundary>} />
          <Route path="/mission" element={<ErrorBoundary><Mission /></ErrorBoundary>} />
          <Route path="/services" element={<ErrorBoundary><Services /></ErrorBoundary>} />
          <Route path="/epc" element={<ErrorBoundary><EPC /></ErrorBoundary>} />
          <Route path="/management" element={<ErrorBoundary><Management /></ErrorBoundary>} />
          <Route path="/credentials" element={<ErrorBoundary><Credentials /></ErrorBoundary>} />
          <Route path="/contact" element={<ErrorBoundary><Contact /></ErrorBoundary>} />
        </Routes>
        <ToastContainer />
      </div>
    </Router>
  );
}

function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <AppContent />
        </ErrorBoundary>
      </QueryClientProvider>
    </Provider>
  );
}

export default App;
